import { useNavigate } from 'react-router-dom'
import { useStore } from 'react-redux'
import { useAppSelector } from '@/store/store-hooks'
import type { RootState } from '@/store/store'
import { selectActiveMatterId } from '@/matter/store/selectors'
import { selectArtifactById } from '@/artifacts/store/selectors'
import { buildConversationPath } from '@/chat-common/conversation-navigation/util/conversation-path-builders'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import type { DraftEntryPoint } from '@/analytics/enums/draft.enums'

type NavigateToDraftInChatArgs = {
  artifactVersionId: string
  conversationId: string
  draftEntryPoint: DraftEntryPoint
}

/**
 * Returns a function that navigates to a draft in its source conversation.
 *
 * Navigates to the conversation with an `?artifactVersionId=<id>` param. The conversation view
 * reads that param on arrival to open the draft in the content viewer and scroll to it.
 *
 * Emits `DraftEntryPointClicked` (observe-only) as the draft re-entry signal. It reads the owning
 * artifact id — stable across edits, unlike the version id the carousel/chip holds — so re-entry
 * joins to `ArtifactGenerationCompleted` in Amplitude. Fire-and-forget; it never blocks navigation.
 */
export function useNavigateToDraftInChat() {
  const navigate = useNavigate()
  const store = useStore<RootState>()
  const { trackEvent } = useAnalytics()
  const matterId = useAppSelector((state: RootState) => selectActiveMatterId(state))

  return ({ artifactVersionId, conversationId, draftEntryPoint }: NavigateToDraftInChatArgs) => {
    if (!matterId) return

    const artifactId = selectArtifactById(store.getState(), { artifactVersionId })?.artifact_id
    if (artifactId) {
      trackEvent(AnalyticsEvent.DraftEntryPointClicked, { artifactId, artifactVersionId, conversationId, matterId, draftEntryPoint })
    }

    navigate(`${buildConversationPath(matterId, conversationId)}?artifactVersionId=${artifactVersionId}`)
  }
}
